/**
 * PDFEditorCanvas — full preview + editor for PDF files.
 *
 * Features:
 *  • Renders every page thumbnail via /api/pdf-tools/preview (server-side PyMuPDF)
 *  • Drag to reorder pages (HTML5 drag-and-drop)
 *  • Rotate individual pages (90° increments)
 *  • Delete pages (soft — reversible via reset)
 *  • Add text annotation (click position on a page → prompt for text)
 *  • Apply → POSTs edit intent to /api/pdf-tools/edit/apply and returns the modified PDF
 *
 * Props:
 *   file: File | { file_data, filename }  — uploaded file
 *   onApplied({ file_data, filename }) — called when user hits Apply
 *   compact: bool — if true, hides annotation controls (used inside smaller tool panes)
 */
import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  RotateCw, Trash2, Type, Loader2, Save, RefreshCw,
  ArrowUp, ArrowDown, GripVertical, Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || '';

function readFileB64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",", 2)[1]);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PDFEditorCanvas({ file, onApplied, compact = false }) {
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [pages, setPages] = useState([]);        // [{ id, origIndex, thumbnail, rotation, deleted }]
  const [order, setOrder] = useState([]);        // page ids in display order
  const [annotations, setAnnotations] = useState([]);  // per-page text overlays
  const [b64, setB64] = useState("");
  const [filename, setFilename] = useState("document.pdf");
  const [selected, setSelected] = useState(null);
  const [dragId, setDragId] = useState(null);

  // Load preview
  const loadPreview = useCallback(async (rawB64, name) => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/pdf-tools/preview`, {
        file_data: rawB64,
        filename: name,
        dpi: 100,
        format: "png",
        max_pages: 100,
      });
      const pp = (r.data?.pages || []).map((p) => ({
        id: `p_${p.index}_${Math.random().toString(36).slice(2, 8)}`,
        origIndex: p.index,
        thumbnail: p.thumbnail,
        width: p.width,
        height: p.height,
        rotation: 0,
        deleted: false,
      }));
      setPages(pp);
      setOrder(pp.map((p) => p.id));
      setAnnotations([]);
      setSelected(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not read PDF");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!file) return;
    (async () => {
      // Accept 3 shapes:
      //   1) { file_data, filename }
      //   2) { name, size, data }  (PDFTools.js shape)
      //   3) raw File / Blob
      if (file.file_data && file.filename) {
        setB64(file.file_data);
        setFilename(file.filename);
        await loadPreview(file.file_data, file.filename);
      } else if (typeof file.data === "string" && file.name) {
        setB64(file.data);
        setFilename(file.name);
        await loadPreview(file.data, file.name);
      } else if (file instanceof Blob || file instanceof File) {
        const raw = await readFileB64(file);
        setB64(raw);
        setFilename(file.name || "document.pdf");
        await loadPreview(raw, file.name || "document.pdf");
      } else {
        toast.error("Unsupported file shape");
      }
    })();
  }, [file, loadPreview]);

  const activePages = order
    .map((id) => pages.find((p) => p.id === id))
    .filter((p) => p && !p.deleted);

  const rotatePage = (id) => {
    setPages((prev) =>
      prev.map((p) => (p.id === id ? { ...p, rotation: (p.rotation + 90) % 360 } : p))
    );
  };

  const deletePage = (id) => {
    setPages((prev) => prev.map((p) => (p.id === id ? { ...p, deleted: true } : p)));
    if (selected === id) setSelected(null);
  };

  const undoDelete = () => {
    setPages((prev) => prev.map((p) => ({ ...p, deleted: false })));
    toast.success("Restored all pages");
  };

  const movePage = (id, dir) => {
    setOrder((prev) => {
      const idx = prev.indexOf(id);
      if (idx < 0) return prev;
      const newIdx = dir === "up" ? idx - 1 : idx + 1;
      if (newIdx < 0 || newIdx >= prev.length) return prev;
      const arr = [...prev];
      [arr[idx], arr[newIdx]] = [arr[newIdx], arr[idx]];
      return arr;
    });
  };

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    setOrder((prev) => {
      const from = prev.indexOf(dragId);
      const to = prev.indexOf(targetId);
      if (from < 0 || to < 0) return prev;
      const arr = [...prev];
      arr.splice(from, 1);
      arr.splice(to, 0, dragId);
      return arr;
    });
    setDragId(null);
  };

  const addAnnotation = (pageId) => {
    const text = window.prompt("Text to add on this page:", "");
    if (!text) return;
    // Default position: center-top
    const displayIdx = activePages.findIndex((p) => p.id === pageId);
    if (displayIdx < 0) return;
    setAnnotations((a) => [
      ...a,
      { pageId, displayIdx, text, x: 0.1, y: 0.9, font_size: 16, color: "#111111" },
    ]);
    toast.success("Text added (top-left)");
  };

  const removeAnnotation = (idx) => {
    setAnnotations((a) => a.filter((_, i) => i !== idx));
  };

  const applyEdits = async () => {
    setApplying(true);
    try {
      const keep_pages = activePages.map((p) => p.origIndex);
      const rotations = {};
      pages.forEach((p) => {
        if (p.rotation && !p.deleted) rotations[String(p.origIndex)] = p.rotation;
      });
      // Rebuild annotations with new (post-reorder) page indices
      const annots = annotations
        .map((a) => {
          const newIdx = activePages.findIndex((p) => p.id === a.pageId);
          if (newIdx < 0) return null;
          return {
            page: newIdx,
            text: a.text,
            x: a.x,
            y: a.y,
            font_size: a.font_size,
            color: a.color,
          };
        })
        .filter(Boolean);

      const r = await axios.post(`${API}/api/pdf-tools/edit/apply`, {
        file_data: b64,
        filename,
        keep_pages,
        rotations,
        annotations: annots,
      });

      const data = r.data;
      toast.success(`Applied — ${data.final_page_count} pages`);
      onApplied?.({
        file_data: data.file_data,
        filename: data.output_filename,
        blob_url: data.file_data,
      });
      // Auto-download
      const link = document.createElement("a");
      link.href = data.file_data;
      link.download = data.output_filename;
      link.click();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  if (!file) return null;

  return (
    <div className="w-full" data-testid="pdf-editor-canvas">
      {/* Toolbar */}
      <div className="flex items-center justify-between gap-3 mb-4 pb-4 border-b border-slate-100">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-[#111111] truncate">{filename}</span>
          <span className="text-xs text-slate-500 shrink-0">
            · {activePages.length}/{pages.length} pages
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {pages.some((p) => p.deleted) && (
            <button
              onClick={undoDelete}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-slate-200 text-xs text-slate-700 hover:bg-slate-50"
              data-testid="pdf-editor-restore"
            >
              <RefreshCw className="w-3 h-3" /> Restore all
            </button>
          )}
          <button
            onClick={applyEdits}
            disabled={applying || activePages.length === 0}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#A855F7] hover:bg-[#9333EA] text-white text-xs font-semibold disabled:opacity-50"
            data-testid="pdf-editor-apply"
          >
            {applying ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
            {applying ? "Applying…" : "Apply & Download"}
          </button>
        </div>
      </div>

      {/* Preview grid */}
      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-[3/4] rounded-2xl bg-slate-100 animate-pulse" />
          ))}
        </div>
      ) : activePages.length === 0 ? (
        <div className="text-center py-12">
          <p className="text-sm text-slate-500">All pages deleted. Click <strong>Restore all</strong> to bring them back.</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-4">
          {activePages.map((p, displayIdx) => {
            const rot = p.rotation || 0;
            const isSelected = selected === p.id;
            return (
              <div
                key={p.id}
                draggable
                onDragStart={() => onDragStart(p.id)}
                onDragOver={onDragOver}
                onDrop={() => onDrop(p.id)}
                onClick={() => setSelected(p.id)}
                data-testid={`pdf-editor-page-${displayIdx}`}
                className={`group relative rounded-2xl overflow-hidden border-2 transition-all cursor-move ${
                  isSelected ? "border-[#A855F7] shadow-[0_0_0_4px_rgba(168,85,247,0.15)]" : "border-slate-200 hover:border-slate-300"
                }`}
              >
                <div className="bg-slate-50 aspect-[3/4] flex items-center justify-center overflow-hidden">
                  <img
                    src={p.thumbnail}
                    alt={`Page ${displayIdx + 1}`}
                    className="max-w-full max-h-full object-contain transition-transform"
                    style={{ transform: `rotate(${rot}deg)` }}
                  />
                </div>
                {/* Page number pill */}
                <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#111111]/80 text-white text-[10px] font-mono">
                  {displayIdx + 1}
                </div>
                {/* Drag handle */}
                <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 bg-white/90 rounded-full p-1">
                  <GripVertical className="w-3 h-3 text-slate-500" />
                </div>
                {/* Action bar */}
                <div className="absolute inset-x-0 bottom-0 flex items-center justify-around bg-white/95 backdrop-blur-sm py-1.5 border-t border-slate-100 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={(e) => { e.stopPropagation(); movePage(p.id, "up"); }}
                    disabled={displayIdx === 0}
                    className="text-slate-600 hover:text-[#A855F7] disabled:opacity-30 p-1"
                    title="Move up"
                  >
                    <ArrowUp className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); movePage(p.id, "down"); }}
                    disabled={displayIdx === activePages.length - 1}
                    className="text-slate-600 hover:text-[#A855F7] disabled:opacity-30 p-1"
                    title="Move down"
                  >
                    <ArrowDown className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={(e) => { e.stopPropagation(); rotatePage(p.id); }}
                    className="text-slate-600 hover:text-[#A855F7] p-1"
                    title="Rotate 90°"
                    data-testid={`pdf-editor-rotate-${displayIdx}`}
                  >
                    <RotateCw className="w-3.5 h-3.5" />
                  </button>
                  {!compact && (
                    <button
                      onClick={(e) => { e.stopPropagation(); addAnnotation(p.id); }}
                      className="text-slate-600 hover:text-[#A855F7] p-1"
                      title="Add text"
                    >
                      <Type className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePage(p.id); }}
                    className="text-slate-600 hover:text-red-500 p-1"
                    title="Delete page"
                    data-testid={`pdf-editor-delete-${displayIdx}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Annotation list */}
      {!compact && annotations.length > 0 && (
        <div className="mt-6 p-4 rounded-2xl border border-slate-200 bg-slate-50">
          <p className="text-xs font-semibold text-slate-700 mb-2">Text annotations ({annotations.length})</p>
          <div className="space-y-1.5 max-h-32 overflow-y-auto">
            {annotations.map((a, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-xs bg-white px-3 py-2 rounded-xl border border-slate-100">
                <span className="truncate">
                  <span className="text-slate-500">Page {a.displayIdx + 1}:</span>{" "}
                  <span className="font-medium text-[#111111]">{a.text}</span>
                </span>
                <button onClick={() => removeAnnotation(i)} className="text-slate-400 hover:text-red-500">
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="text-[11px] text-slate-400 mt-4">
        Tip: drag any page thumbnail to reorder. Hover for rotate / delete / add-text.
      </p>
    </div>
  );
}
